// src/pages/auth/Register.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Link,
  Grid,
  InputAdornment,
  IconButton,
  Alert,
  Avatar,
  Fade,
  Slide,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Tooltip,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Phone,
  School,
  PersonAdd,
  ChildCare,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../../firebase';
import { doc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ThemeProvider, createTheme } from '@mui/material/styles';

// Tema personalizado con paleta verde pastel (igual que Login)
const pastelGreenTheme = createTheme({
  palette: {
    primary: {
      main: '#81C784',
      light: '#A5D6A7',
      dark: '#66BB6A',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#C8E6C9',
      light: '#E8F5E8',
      dark: '#A5D6A7',
      contrastText: '#2E7D32',
    },
    background: {
      default: '#F1F8E9',
      paper: '#FFFFFF',
    },
    success: {
      main: '#4CAF50',
      light: '#81C784',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 8px 32px rgba(129, 199, 132, 0.15)',
          borderRadius: '16px',
          border: '1px solid #E8F5E8',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
          },
        },
      },
    },
  },
});

const Register = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Datos del usuario
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  // Datos de los hijos
  const [children, setChildren] = useState([{
    nombre: '',
    apellidos: '',
    curso: '',
    fechaNacimiento: '',
    alergias: '',
    necesidadesEspeciales: '',
    personasAutorizadas: '',
    observaciones: ''
  }]);

  const steps = ['Datos del Responsable', 'Datos de los Hijos'];

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChildChange = (index, field, value) => {
    const newChildren = [...children];
    newChildren[index][field] = value;
    setChildren(newChildren);
  };

  const addChild = () => {
    setChildren([...children, {
      nombre: '',
      apellidos: '',
      curso: '',
      fechaNacimiento: '',
      alergias: '',
      necesidadesEspeciales: '',
      personasAutorizadas: '',
      observaciones: ''
    }]);
  };

  const removeChild = (index) => {
    if (children.length > 1) {
      const newChildren = children.filter((_, i) => i !== index);
      setChildren(newChildren);
    }
  };

  const validateStep1 = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setError('Por favor, completa todos los campos obligatorios');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Formato de email inválido');
      return false;
    }

    if (!formData.password || formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }

    setError('');
    return true;
  };

  const validateStep2 = () => {
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (!child.nombre || !child.apellidos || !child.curso) {
        setError(`Por favor, completa los datos obligatorios del hijo ${i + 1}`);
        return false;
      }
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) {
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep2()) {
      return;
    }

    setLoading(true);
    setError('');

    let createdUser = null;

    try {
      // 1. Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      createdUser = userCredential.user;

      // 2. Crear documento del usuario en Firestore
      const userDocRef = doc(db, 'users', createdUser.uid);
      await setDoc(userDocRef, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: 'user',
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // 3. Crear documentos de los hijos
      const childrenCollection = collection(db, 'children');
      for (const child of children) {
        const childData = {
          nombre: child.nombre,
          apellidos: child.apellidos,
          curso: child.curso,
          fechaNacimiento: child.fechaNacimiento || '',
          alergias: child.alergias || '',
          necesidadesEspeciales: child.necesidadesEspeciales || '',
          personasAutorizadas: child.personasAutorizadas || '',
          observaciones: child.observaciones || '',
          parentId: createdUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        await addDoc(childrenCollection, childData);
      }

      // 4. Navegar al dashboard del usuario
      setTimeout(() => {
        navigate('/portal', { replace: true });
      }, 1000);

    } catch (error) {
      console.error('Error en el registro:', error);

      // Si el Auth user fue creado pero Firestore fallo, limpiar el Auth user
      if (createdUser) {
        try {
          await createdUser.delete();
        } catch (deleteError) {
          console.error('Error al limpiar usuario Auth:', deleteError);
        }
      }

      let errorMessage = 'Error al crear la cuenta';

      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Ya existe una cuenta con este correo electrónico';
          break;
        case 'auth/invalid-email':
          errorMessage = 'El formato del correo electrónico no es válido';
          break;
        case 'auth/weak-password':
          errorMessage = 'La contraseña es demasiado débil';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Error de conexión. Verifica tu conexión a internet';
          break;
        default:
          errorMessage = 'Error al crear la cuenta. Inténtalo de nuevo';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom color="text.primary" fontWeight={600}>
              Información del Responsable
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Nombre"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Apellidos"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Correo electrónico"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Teléfono"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Contraseña"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={loading}
                  helperText="Mínimo 6 caracteres"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={loading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Confirmar contraseña"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          disabled={loading}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" color="text.primary" fontWeight={600}>
                Información de los Hijos
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addChild}
                variant="outlined"
                size="small"
                disabled={loading}
              >
                Agregar Hijo
              </Button>
            </Box>

            {children.map((child, index) => (
              <Paper
                key={index}
                elevation={2}
                sx={{
                  p: 3,
                  mb: 3,
                  backgroundColor: 'rgba(129, 199, 132, 0.05)',
                  border: '1px solid rgba(129, 199, 132, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChildCare color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Hijo {index + 1}
                    </Typography>
                  </Box>
                  {children.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => removeChild(index)}
                      disabled={loading}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Nombre"
                      value={child.nombre}
                      onChange={(e) => handleChildChange(index, 'nombre', e.target.value)}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Apellidos"
                      value={child.apellidos}
                      onChange={(e) => handleChildChange(index, 'apellidos', e.target.value)}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Curso"
                      value={child.curso}
                      onChange={(e) => handleChildChange(index, 'curso', e.target.value)}
                      disabled={loading}
                      placeholder="Ej: 1º infantil"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Fecha de Nacimiento"
                      type="date"
                      value={child.fechaNacimiento}
                      onChange={(e) => handleChildChange(index, 'fechaNacimiento', e.target.value)}
                      disabled={loading}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Alergias"
                      value={child.alergias}
                      onChange={(e) => handleChildChange(index, 'alergias', e.target.value)}
                      disabled={loading}
                      multiline
                      rows={2}
                      placeholder="Indica si tiene alguna alergia"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Necesidades Especiales"
                      value={child.necesidadesEspeciales}
                      onChange={(e) => handleChildChange(index, 'necesidadesEspeciales', e.target.value)}
                      disabled={loading}
                      multiline
                      rows={2}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Personas Autorizadas"
                      value={child.personasAutorizadas}
                      onChange={(e) => handleChildChange(index, 'personasAutorizadas', e.target.value)}
                      disabled={loading}
                      multiline
                      rows={2}
                      placeholder="Nombres de personas autorizadas para recoger al niño"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Observaciones"
                      value={child.observaciones}
                      onChange={(e) => handleChildChange(index, 'observaciones', e.target.value)}
                      disabled={loading}
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={pastelGreenTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #F1F8E9 0%, #E8F5E8 50%, #C8E6C9 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="md">
          <Fade in={mounted} timeout={800}>
            <Card
              elevation={0}
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: '24px',
                border: '1px solid rgba(129, 199, 132, 0.2)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #81C784 0%, #66BB6A 100%)',
                  py: 4,
                  px: 3,
                  textAlign: 'center',
                }}
              >
                <Slide direction="down" in={mounted} timeout={600}>
                  <Box>
                    <Avatar
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        color: 'primary.main',
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                      }}
                    >
                      <PersonAdd sx={{ fontSize: 40 }} />
                    </Avatar>

                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        mb: 1,
                      }}
                    >
                      Registro de Usuario
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <School sx={{ fontSize: 20 }} />
                      AMPA del Bosc
                    </Typography>
                  </Box>
                </Slide>
              </Box>

              <CardContent sx={{ p: 4 }}>
                <Slide direction="up" in={mounted} timeout={800}>
                  <Box>
                    {/* Stepper */}
                    <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                      {steps.map((label) => (
                        <Step key={label}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>

                    {error && (
                      <Fade in={!!error}>
                        <Alert
                          severity="error"
                          sx={{ mb: 3 }}
                          onClose={() => setError('')}
                        >
                          {error}
                        </Alert>
                      </Fade>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                      {renderStepContent(activeStep)}

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                        {activeStep === 0 ? (
                          <Button
                            component={RouterLink}
                            to="/login"
                            disabled={loading}
                          >
                            Volver al Login
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            onClick={handleBack}
                            disabled={loading}
                          >
                            Atrás
                          </Button>
                        )}

                        {activeStep === 0 ? (
                          <Button
                            type="button"
                            variant="contained"
                            onClick={handleNext}
                            disabled={loading}
                          >
                            Siguiente
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            startIcon={loading && <CircularProgress size={20} />}
                          >
                            {loading ? 'Registrando...' : 'Crear Cuenta'}
                          </Button>
                        )}
                      </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        ¿Ya tienes una cuenta?{' '}
                        <Link
                          component={RouterLink}
                          to="/login"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Inicia sesión aquí
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                </Slide>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Register;
